import React from "react";
import { formatINR } from "../../lib/format";
import { findBandById } from "../../lib/budget-bands";

/**
 * Read-only assembled-payload summary shown when buyer taps "View draft summary"
 * inside the Phase-3 placeholder. Useful for QA + buyer reassurance.
 */
export default function DraftSummary({ draftId, payload, onBack }) {
  const p = payload || {};
  const band = p?.budget?.band ? findBandById(p.budget.band) : null;

  return (
    <section
      data-testid="draft-summary"
      className="mx-auto max-w-2xl px-5 pt-10 pb-24 sm:px-6 sm:pt-16"
    >
      <p className="text-[10px] uppercase tracking-[0.3em] text-[#C8A24B]">
        Draft saved
      </p>
      <h1 className="mt-3 font-display text-3xl text-[#1A1A1A] sm:text-4xl">
        Your brief, in summary
      </h1>
      <p className="mt-2 text-xs text-[#1A1A1A]/50">
        Draft ID:{" "}
        <span data-testid="draft-summary-id" className="font-mono text-[#1A1A1A]/75">
          {draftId || "—"}
        </span>
      </p>

      <Row label="Occasion" value={p.occasion || "—"} />
      {p.occasion_subtypes?.length ? (
        <Row label="Wedding sub-events" value={p.occasion_subtypes.join(", ")} />
      ) : null}
      <Row label="Date mode" value={p.date?.mode || "—"} />
      {p.date?.exact ? <Row label="Exact date" value={p.date.exact} /> : null}
      {p.date?.range_start ? (
        <Row
          label="Date range"
          value={`${p.date.range_start} → ${p.date.range_end || "?"}`}
        />
      ) : null}
      {p.date?.month && p.date?.year ? (
        <Row label="Flexible month" value={`${p.date.month}/${p.date.year}`} />
      ) : null}
      <Row label="Location mode" value={p.location_mode || "—"} />
      {p.locations?.length ? (
        <Row
          label={`Locations (${p.locations.length})`}
          value={p.locations
            .map(
              (l) =>
                `${l.venue_name || ""}${l.venue_name ? " · " : ""}${l.city || ""}`,
            )
            .join(" | ")}
        />
      ) : null}
      {p.destination ? <Row label="Destination" value={p.destination} /> : null}

      <h2 className="mt-10 font-display text-xl text-[#1A1A1A]">Scope</h2>
      {p.scope && Object.keys(p.scope).length ? (
        <ul className="mt-3 space-y-2 text-sm">
          {Object.entries(p.scope).map(([k, v]) => (
            <li key={k} className="border-b border-[#EAE3D2] pb-2">
              <span className="text-[#1A1A1A]/55">{k}: </span>
              <span className="text-[#1A1A1A]">
                {Array.isArray(v) ? v.join(", ") : String(v)}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-3 text-sm text-[#1A1A1A]/55">No scope answers yet.</p>
      )}

      <h2 className="mt-10 font-display text-xl text-[#1A1A1A]">References</h2>
      <p className="mt-2 text-sm text-[#1A1A1A]/70">
        Uploaded images: {p?.references?.images?.length || 0} · Liked portfolio:{" "}
        {p?.references?.liked_portfolio_ids?.length || 0}
      </p>

      <h2 className="mt-10 font-display text-xl text-[#1A1A1A]">Budget</h2>
      {p?.budget?.mode === "band" && band ? (
        <p className="mt-2 text-sm text-[#1A1A1A]/80">
          {band.label} — {formatINR(band.min)}
          {band.max ? ` – ${formatINR(band.max)}` : "+"}
        </p>
      ) : p?.budget?.mode === "max_amount" ? (
        <p className="mt-2 text-sm text-[#1A1A1A]/80">
          Up to {formatINR(p.budget.max_amount)}
        </p>
      ) : p?.budget?.mode ? (
        <p className="mt-2 text-sm text-[#1A1A1A]/80">{p.budget.mode}</p>
      ) : (
        <p className="mt-2 text-sm text-[#1A1A1A]/55">Not specified</p>
      )}

      <h2 className="mt-10 font-display text-xl text-[#1A1A1A]">Contact</h2>
      <Row label="Name" value={p?.contact?.full_name || "—"} small />
      <Row label="Email" value={p?.contact?.email || "—"} small />
      <Row
        label="Phone"
        value={
          p?.contact?.phone
            ? `${p?.contact?.country_code || "+91"} ${p.contact.phone}`
            : "—"
        }
        small
      />
      <Row
        label="Best time to reach"
        value={p?.contact?.best_time || "—"}
        small
      />
      {p?.contact?.notes ? <Row label="Notes" value={p.contact.notes} small /> : null}

      <button
        type="button"
        data-testid="draft-summary-back"
        onClick={onBack}
        className="mt-10 inline-flex items-center rounded-full border border-[#1A1A1A] px-6 py-2.5 text-xs uppercase tracking-[0.22em] text-[#1A1A1A] transition hover:bg-[#1A1A1A] hover:text-[#FAF7F2]"
      >
        Back to the builder
      </button>
    </section>
  );
}

function Row({ label, value, small }) {
  return (
    <div
      className={[
        "mt-4 border-b border-[#EAE3D2] pb-3 text-sm",
        small ? "" : "",
      ].join(" ")}
    >
      <p className="text-[10px] uppercase tracking-[0.24em] text-[#1A1A1A]/45">
        {label}
      </p>
      <p className="mt-1 text-[#1A1A1A]">{value}</p>
    </div>
  );
}
